import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import OptionsPanel from '../components/OptionsPanel';
import Kozijnen from '../components/Kozijnen';
import Summary from '../components/Summary';
import Quote from '../components/Quote';
import SavedQuotes from '../components/SavedQuotes';
import { useTranslation } from '../context/TranslationContext';
import {
  windowTypes,
  profileSystems,
  glassTextures,
  glazingTypes,
  colors,
  openingTypes,
  hardware,
  accessories,
  DIMENSION_CONSTRAINTS,
  partsOptions,
  overheadOptions,
  woodJointOptions,
  getColorById,
  divisionTypes,
  raamtypes,
  PRICING
} from '../data/mockData';

const Configurator = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [showSummary, setShowSummary] = useState(true);
  const [showQuote, setShowQuote] = useState(false);
  const [showSavedQuotes, setShowSavedQuotes] = useState(false);
  const drawingBoardRef = useRef(null);
  
  const [config, setConfig] = useState({
    width: 1500,
    height: 1500,
    profileSystem: 'synego',
    glassTexture: 'helder-glas',
    glazingType: 'double',
    color: 'anthracite',
    interiorFrameColor: 'white',
    openingType: 'none',
    hardware: [],
    accessories: [],
    parts: 1,
    quantity: 1,
    overhead: 'no-overhead',
    overheadParts: 1, // Number of parts in overhead area (1-5)
    overheadHeight: 300, // Overhead height in mm
    underlight: 'no-underlight',
    underlightParts: 1, // Number of parts in underlight area (1-5)
    underlightHeight: 400, // Underlight height in mm
    woodJoint: 'joint-45',
    divisionType: 'equal',
    raamtype: 'fixed-sash',
    rampDorpel: 'no-ramp-dorpel', // Ramp dorpel (sloped threshold) option
    // Array to store window type for each part (index 0 = part 1, index 1 = part 2, etc.)
    partWindowTypes: ['fixed-sash'], // Default: first part has fixed-sash
    // Per-part widths in mm (must always sum to total width)
    partWidths: [1500],
    // Array to store window type for each overhead part
    overheadPartWindowTypes: ['fixed-sash'], // Default: first overhead part has fixed-sash
    // Per-overhead-part widths in mm (must always sum to total width)
    overheadPartWidths: [1500],
    // Array to store window type for each underlight part (mirrors overhead behaviour)
    underlightPartWindowTypes: ['fixed-sash'],
    // Per-underlight-part widths in mm (must always sum to total width)
    underlightPartWidths: [1500],
    // Panel colors for main parts, overhead parts, and underlight parts
    panelColors: [],
    panelOverheadColors: [],
    panelUnderlightColors: [],
    // Panel line types: 'none', 'horizontal', 'vertical'
    panelLineTypes: [],
    panelOverheadLineTypes: [],
    panelUnderlightLineTypes: [],
    // Track whether the user has manually overridden width
    widthManuallySet: false,
  });
  
  // Track the current quantity input value from Summary component
  // Initialize to config.quantity (which defaults to 1)
  const [currentQuantityInput, setCurrentQuantityInput] = useState(config.quantity ?? 1);
  
  // Sync currentQuantityInput when config.quantity changes
  useEffect(() => {
    setCurrentQuantityInput(config.quantity ?? 1);
  }, [config.quantity]);
  
  const handleConfigChange = (field, value) => {
    setConfig(prev => {
      const newConfig = { ...prev, [field]: value };
      
      // If user manually edits width, remember so we don't auto-adjust on parts change
      if (field === 'width') {
        newConfig.widthManuallySet = true;

        // Rescale existing part widths proportionally to match new total width
        const currentWidths = prev.partWidths && prev.partWidths.length
          ? prev.partWidths
          : [prev.width || value];
        const totalBefore = currentWidths.reduce((sum, w) => sum + w, 0) || 1;
        const scaleFactor = value / totalBefore;
        let scaled = currentWidths.map(w => Math.round(w * scaleFactor));
        // Adjust last segment so the sum matches exactly
        const sumAfter = scaled.reduce((sum, w) => sum + w, 0);
        const diff = value - sumAfter;
        scaled[scaled.length - 1] += diff;
        newConfig.partWidths = scaled;

        // Also rescale overhead part widths proportionally to match new total width
        if (prev.overhead === 'overhead') {
          const overheadParts = prev.overheadParts || 1;
          const currentOverheadWidths = prev.overheadPartWidths && prev.overheadPartWidths.length === overheadParts
            ? prev.overheadPartWidths
            : Array(overheadParts).fill(Math.floor((prev.width || value) / overheadParts));
          const overheadTotalBefore = currentOverheadWidths.reduce((sum, w) => sum + w, 0) || 1;
          const overheadScaleFactor = value / overheadTotalBefore;
          let overheadScaled = currentOverheadWidths.map(w => Math.round(w * overheadScaleFactor));
          // Adjust last segment so the sum matches exactly
          const overheadSumAfter = overheadScaled.reduce((sum, w) => sum + w, 0);
          const overheadDiff = value - overheadSumAfter;
          overheadScaled[overheadParts - 1] += overheadDiff;
          newConfig.overheadPartWidths = overheadScaled;
        }

        // Also rescale underlight part widths proportionally to match new total width
        if (prev.underlight === 'underlight') {
          const underlightParts = prev.underlightParts || 1;
          const currentUnderlightWidths = prev.underlightPartWidths && prev.underlightPartWidths.length === underlightParts
            ? prev.underlightPartWidths
            : Array(underlightParts).fill(Math.floor((prev.width || value) / underlightParts));
          const underlightTotalBefore = currentUnderlightWidths.reduce((sum, w) => sum + w, 0) || 1;
          const underlightScaleFactor = value / underlightTotalBefore;
          let underlightScaled = currentUnderlightWidths.map(w => Math.round(w * underlightScaleFactor));
          // Adjust last segment so the sum matches exactly
          const underlightSumAfter = underlightScaled.reduce((sum, w) => sum + w, 0);
          const underlightDiff = value - underlightSumAfter;
          underlightScaled[underlightParts - 1] += underlightDiff;
          newConfig.underlightPartWidths = underlightScaled;
        }
      }
      
      // When parts change, adjust the partWindowTypes array
      if (field === 'parts') {
        const currentParts = prev.parts || 1;
        const newParts = value;
        const currentTypes = prev.partWindowTypes || ['fixed-glass'];
        
        if (newParts > currentParts) {
          // Add more parts - duplicate the last window type
          const lastType = currentTypes[currentTypes.length - 1] || 'fixed-sash';
          newConfig.partWindowTypes = [...currentTypes, ...Array(newParts - currentParts).fill(lastType)];
        } else if (newParts < currentParts) {
          // Remove parts - keep only the first N
          newConfig.partWindowTypes = currentTypes.slice(0, newParts);
        }

        // Recalculate per-part widths for the new part count
        const targetWidth = prev.width || DIMENSION_CONSTRAINTS.MIN_WIDTH;
        const baseWidth = Math.floor(targetWidth / newParts);
        const partWidths = Array(newParts).fill(baseWidth);
        // Distribute any remainder to the last part so sum matches total width
        const remainder = targetWidth - baseWidth * newParts;
        partWidths[newParts - 1] += remainder;
        newConfig.partWidths = partWidths;
        
        // Initialize partWindowTypes with fixed-sash if not already set
        if (!newConfig.partWindowTypes || newConfig.partWindowTypes.length !== newParts) {
          newConfig.partWindowTypes = Array(newParts).fill('fixed-sash');
        }
        
        // Initialize panel colors and line types arrays to match new parts count
        const currentPanelColors = prev.panelColors || [];
        const currentPanelLineTypes = prev.panelLineTypes || [];
        if (newParts > currentPanelColors.length) {
          newConfig.panelColors = [...currentPanelColors, ...Array(newParts - currentPanelColors.length).fill(null)];
        } else {
          newConfig.panelColors = currentPanelColors.slice(0, newParts);
        }
        if (newParts > currentPanelLineTypes.length) {
          newConfig.panelLineTypes = [...currentPanelLineTypes, ...Array(newParts - currentPanelLineTypes.length).fill('none')];
        } else {
          newConfig.panelLineTypes = currentPanelLineTypes.slice(0, newParts);
        }

        // Auto-adjust width based on number of parts IF the user hasn't overridden width
        if (!prev.widthManuallySet) {
          const suggestedWidth = Math.min(
            DIMENSION_CONSTRAINTS.MAX_WIDTH,
            Math.max(DIMENSION_CONSTRAINTS.MIN_WIDTH, newParts * 1000)
          );
          newConfig.width = suggestedWidth;

          // Update partWidths again to reflect the new total width
          const baseWidth2 = Math.floor(suggestedWidth / newParts);
          const partWidths2 = Array(newParts).fill(baseWidth2);
          const remainder2 = suggestedWidth - baseWidth2 * newParts;
          partWidths2[newParts - 1] += remainder2;
          newConfig.partWidths = partWidths2;
        }
      }
      
      // When overhead parts change, adjust the overheadPartWindowTypes array
      if (field === 'overheadParts') {
        const currentOverheadParts = prev.overheadParts || 1;
        const newOverheadParts = value;
        const currentTypes = prev.overheadPartWindowTypes || ['fixed-sash'];
        
        if (newOverheadParts > currentOverheadParts) {
          // Add more parts - duplicate the last window type
          const lastType = currentTypes[currentTypes.length - 1] || 'fixed-sash';
          newConfig.overheadPartWindowTypes = [...currentTypes, ...Array(newOverheadParts - currentOverheadParts).fill(lastType)];
        } else if (newOverheadParts < currentOverheadParts) {
          // Remove parts - keep only the first N
          newConfig.overheadPartWindowTypes = currentTypes.slice(0, newOverheadParts);
        }

        // Recalculate per-overhead-part widths for the new part count
        const targetWidth = prev.width || DIMENSION_CONSTRAINTS.MIN_WIDTH;
        const baseWidth = Math.floor(targetWidth / newOverheadParts);
        const overheadPartWidths = Array(newOverheadParts).fill(baseWidth);
        // Distribute any remainder to the last part so sum matches total width
        const remainder = targetWidth - baseWidth * newOverheadParts;
        overheadPartWidths[newOverheadParts - 1] += remainder;
        newConfig.overheadPartWidths = overheadPartWidths;
        
        // Initialize overheadPartWindowTypes with fixed-sash if not already set
        if (!newConfig.overheadPartWindowTypes || newConfig.overheadPartWindowTypes.length !== newOverheadParts) {
          newConfig.overheadPartWindowTypes = Array(newOverheadParts).fill('fixed-sash');
        }
        
        // Initialize overhead panel colors and line types arrays to match new overhead parts count
        const currentOverheadPanelColors = prev.panelOverheadColors || [];
        const currentOverheadPanelLineTypes = prev.panelOverheadLineTypes || [];
        if (newOverheadParts > currentOverheadPanelColors.length) {
          newConfig.panelOverheadColors = [...currentOverheadPanelColors, ...Array(newOverheadParts - currentOverheadPanelColors.length).fill(null)];
        } else {
          newConfig.panelOverheadColors = currentOverheadPanelColors.slice(0, newOverheadParts);
        }
        if (newOverheadParts > currentOverheadPanelLineTypes.length) {
          newConfig.panelOverheadLineTypes = [...currentOverheadPanelLineTypes, ...Array(newOverheadParts - currentOverheadPanelLineTypes.length).fill('none')];
        } else {
          newConfig.panelOverheadLineTypes = currentOverheadPanelLineTypes.slice(0, newOverheadParts);
        }
      }
      
      // When overhead changes, reset overhead parts to 1 if switching to no-overhead
      if (field === 'overhead' && value === 'no-overhead') {
        newConfig.overheadParts = 1;
        newConfig.overheadPartWindowTypes = ['fixed-sash'];
        newConfig.overheadPartWidths = [prev.width || 1500];
      }
      
      // When overhead is first selected, initialize overhead part widths and height
      if (field === 'overhead' && value === 'overhead' && prev.overhead === 'no-overhead') {
        const overheadParts = prev.overheadParts || 1;
        const targetWidth = prev.width || DIMENSION_CONSTRAINTS.MIN_WIDTH;
        const baseWidth = Math.floor(targetWidth / overheadParts);
        const overheadPartWidths = Array(overheadParts).fill(baseWidth);
        // Distribute any remainder to the last part so sum matches total width
        const remainder = targetWidth - baseWidth * overheadParts;
        overheadPartWidths[overheadParts - 1] += remainder;
        newConfig.overheadPartWidths = overheadPartWidths;
        // Initialize overhead height to 20% of current height, clamped to 400-800
        const initialOverheadHeight = Math.round((prev.height || 1500) * 0.20);
        newConfig.overheadHeight = Math.max(400, Math.min(800, initialOverheadHeight));
      }
      
      if (field === 'overheadHeight') {
        const totalHeight = prev.height || DIMENSION_CONSTRAINTS.MIN_HEIGHT;
        const maxHalf = Math.floor(totalHeight / 2);
        const minOverhead = 150;
        const clampedOverheadHeight = Math.max(minOverhead, Math.min(maxHalf, value));
        newConfig.overheadHeight = clampedOverheadHeight;
      }
      
      if (field === 'underlightHeight') {
        const totalHeight = prev.height || DIMENSION_CONSTRAINTS.MIN_HEIGHT;
        const maxHalf = Math.floor(totalHeight / 2);
        const minUnder = 150;
        const clampedUnderlightHeight = Math.max(minUnder, Math.min(maxHalf, value));
        newConfig.underlightHeight = clampedUnderlightHeight;
      }
      
      // When underlight parts change (bottom fixed sections)
      if (field === 'underlightParts') {
        const newUnderlightParts = value;
        newConfig.underlightParts = newUnderlightParts;
        // Adjust underlightPartWindowTypes to match new count
        const currentUnderlightParts = prev.underlightParts || 1;
        const currentTypes = prev.underlightPartWindowTypes || ['fixed-sash'];
        if (newUnderlightParts > currentUnderlightParts) {
          const lastType = currentTypes[currentTypes.length - 1] || 'fixed-sash';
          newConfig.underlightPartWindowTypes = [
            ...currentTypes,
            ...Array(newUnderlightParts - currentUnderlightParts).fill(lastType),
          ];
        } else if (newUnderlightParts < currentUnderlightParts) {
          newConfig.underlightPartWindowTypes = currentTypes.slice(0, newUnderlightParts);
        }
        
        // Initialize underlight panel colors and line types arrays to match new underlight parts count
        const currentUnderlightPanelColors = prev.panelUnderlightColors || [];
        const currentUnderlightPanelLineTypes = prev.panelUnderlightLineTypes || [];
        if (newUnderlightParts > currentUnderlightPanelColors.length) {
          newConfig.panelUnderlightColors = [...currentUnderlightPanelColors, ...Array(newUnderlightParts - currentUnderlightPanelColors.length).fill(null)];
        } else {
          newConfig.panelUnderlightColors = currentUnderlightPanelColors.slice(0, newUnderlightParts);
        }
        if (newUnderlightParts > currentUnderlightPanelLineTypes.length) {
          newConfig.panelUnderlightLineTypes = [...currentUnderlightPanelLineTypes, ...Array(newUnderlightParts - currentUnderlightPanelLineTypes.length).fill('none')];
        } else {
          newConfig.panelUnderlightLineTypes = currentUnderlightPanelLineTypes.slice(0, newUnderlightParts);
        }
        if (!newConfig.underlightPartWindowTypes || newConfig.underlightPartWindowTypes.length !== newUnderlightParts) {
          newConfig.underlightPartWindowTypes = Array(newUnderlightParts).fill('fixed-sash');
        }
        // Adjust underlightPartWidths to match new count (equal widths)
        const totalWidth = prev.width || 1500;
        newConfig.underlightPartWidths = Array(newUnderlightParts).fill(Math.floor(totalWidth / newUnderlightParts));
      }
      
      // When underlight toggles off, reset parts and restore default height
      if (field === 'underlight' && value === 'no-underlight') {
        newConfig.underlightParts = 1;
        newConfig.underlightHeight = 400;
        newConfig.underlightPartWindowTypes = ['fixed-sash'];
        newConfig.underlightPartWidths = [prev.width || 1500];
      }
      
      // Reset opening type if raamtype changes (for single part mode)
      if (field === 'raamtype') {
        const validOpenings = openingTypes.filter(ot => ot.validFor.includes(value));
        newConfig.openingType = validOpenings[0]?.id || 'none';
        // Clear hardware and accessories that don't apply
        newConfig.hardware = newConfig.hardware.filter(h => 
          hardware.find(hw => hw.id === h)?.applicableTo.includes(value)
        );
        newConfig.accessories = newConfig.accessories.filter(a => 
          accessories.find(acc => acc.id === a)?.applicableTo.includes(value)
        );
        // When parts === 1, also update partWindowTypes[0] to keep them in sync
        if (prev.parts === 1) {
          newConfig.partWindowTypes = [value];
        }
      }
      
      return newConfig;
    });
  };
  
  // Handle window type change for a specific part
  const handlePartWindowTypeChange = (partIndex, windowTypeId) => {
    setConfig(prev => {
      const parts = prev.parts || 1;
      // Ensure the array has the correct length, filling with 'fixed-sash' if needed
      const currentTypes = prev.partWindowTypes || [];
      const newPartWindowTypes = Array(parts)
        .fill(null)
        .map((_, idx) => currentTypes[idx] || 'fixed-sash');
      newPartWindowTypes[partIndex] = windowTypeId;
      return { ...prev, partWindowTypes: newPartWindowTypes };
    });
  };
  
  // Handle window type change for a specific overhead part
  const handleOverheadPartWindowTypeChange = (partIndex, windowTypeId) => {
    setConfig(prev => {
      const newOverheadPartWindowTypes = [...(prev.overheadPartWindowTypes || [])];
      newOverheadPartWindowTypes[partIndex] = windowTypeId;
      return { ...prev, overheadPartWindowTypes: newOverheadPartWindowTypes };
    });
  };

  // Handle window type change for a specific underlight part
  const handleUnderlightPartWindowTypeChange = (partIndex, windowTypeId) => {
    setConfig(prev => {
      const underParts = prev.underlightParts || 1;
      const currentTypes = prev.underlightPartWindowTypes || [];
      const newUnderlightPartWindowTypes = Array(underParts)
        .fill(null)
        .map((_, idx) => currentTypes[idx] || 'fixed-sash');
      newUnderlightPartWindowTypes[partIndex] = windowTypeId;
      return { ...prev, underlightPartWindowTypes: newUnderlightPartWindowTypes };
    });
  };

  // Handle per-part width change from the drawing board
  const handlePartWidthChange = (partIndex, newWidthMm) => {
    setConfig(prev => {
      const parts = prev.parts || 1;
      const totalWidth = prev.width || 0;
      if (parts <= 1 || !totalWidth) return prev;

      // Minimum width per part (in mm)
      const minPerPart = 500;
      const maxPerPart = DIMENSION_CONSTRAINTS.MAX_WIDTH;

      const currentWidths = prev.partWidths && prev.partWidths.length === parts
        ? [...prev.partWidths]
        : Array(parts).fill(Math.floor(totalWidth / parts));

      // Clamp requested width so we always leave room for at least minPerPart on others
      const otherMinTotal = minPerPart * (parts - 1);
      const maxAllowedForThis = Math.min(maxPerPart, totalWidth - otherMinTotal);
      const minAllowedForThis = minPerPart;
      const clampedNew = Math.max(minAllowedForThis, Math.min(maxAllowedForThis, newWidthMm));

      const old = currentWidths[partIndex];
      const delta = clampedNew - old;
      if (delta === 0) return prev;

      currentWidths[partIndex] = clampedNew;

      // Adjust the next part (or previous if this is the last) to compensate
      const adjustIndex = partIndex < parts - 1 ? partIndex + 1 : partIndex - 1;
      if (adjustIndex >= 0) {
        currentWidths[adjustIndex] = Math.max(
          minPerPart,
          currentWidths[adjustIndex] - delta
        );
      }

      // Normalize so sum exactly equals totalWidth by tweaking the last segment
      let sum = currentWidths.reduce((s, w) => s + w, 0);
      const diff = totalWidth - sum;
      currentWidths[parts - 1] = Math.max(
        minPerPart,
        currentWidths[parts - 1] + diff
      );

      return {
        ...prev,
        partWidths: currentWidths,
        widthManuallySet: true, // user effectively changed widths
      };
    });
  };
  
  // Handle per-overhead-part width change from the drawing board
  const handleOverheadPartWidthChange = (partIndex, newWidthMm) => {
    setConfig(prev => {
      const overheadParts = prev.overheadParts || 1;
      const totalWidth = prev.width || 0;
      if (overheadParts <= 1 || !totalWidth) return prev;

      // Minimum width per part (in mm)
      const minPerPart = 500;
      const maxPerPart = DIMENSION_CONSTRAINTS.MAX_WIDTH;

      const currentWidths = prev.overheadPartWidths && prev.overheadPartWidths.length === overheadParts
        ? [...prev.overheadPartWidths]
        : Array(overheadParts).fill(Math.floor(totalWidth / overheadParts));

      // Clamp requested width so we always leave room for at least minPerPart on others
      const otherMinTotal = minPerPart * (overheadParts - 1);
      const maxAllowedForThis = Math.min(maxPerPart, totalWidth - otherMinTotal);
      const minAllowedForThis = minPerPart;
      const clampedNew = Math.max(minAllowedForThis, Math.min(maxAllowedForThis, newWidthMm));

      const old = currentWidths[partIndex];
      const delta = clampedNew - old;
      if (delta === 0) return prev;

      currentWidths[partIndex] = clampedNew;

      // Adjust the next part (or previous if this is the last) to compensate
      const adjustIndex = partIndex < overheadParts - 1 ? partIndex + 1 : partIndex - 1;
      if (adjustIndex >= 0) {
        currentWidths[adjustIndex] = Math.max(
          minPerPart,
          currentWidths[adjustIndex] - delta
        );
      }

      // Normalize so sum exactly equals totalWidth by tweaking the last segment
      let sum = currentWidths.reduce((s, w) => s + w, 0);
      const diff = totalWidth - sum;
      currentWidths[overheadParts - 1] = Math.max(
        minPerPart,
        currentWidths[overheadParts - 1] + diff
      );

      return {
        ...prev,
        overheadPartWidths: currentWidths,
        widthManuallySet: true, // user effectively changed widths
      };
    });
  };
  
  // Handle per-underlight-part width change from the drawing board
  const handleUnderlightPartWidthChange = (partIndex, newWidthMm) => {
    setConfig(prev => {
      const underlightParts = prev.underlightParts || 1;
      const totalWidth = prev.width || 0;
      if (underlightParts <= 1 || !totalWidth) return prev;

      // Minimum width per part (in mm)
      const minPerPart = 500;
      const maxPerPart = DIMENSION_CONSTRAINTS.MAX_WIDTH;

      const currentWidths = prev.underlightPartWidths && prev.underlightPartWidths.length === underlightParts
        ? [...prev.underlightPartWidths]
        : Array(underlightParts).fill(Math.floor(totalWidth / underlightParts));

      // Clamp requested width so we always leave room for at least minPerPart on others
      const otherMinTotal = minPerPart * (underlightParts - 1);
      const maxAllowedForThis = Math.min(maxPerPart, totalWidth - otherMinTotal);
      const minAllowedForThis = minPerPart;
      const clampedNew = Math.max(minAllowedForThis, Math.min(maxAllowedForThis, newWidthMm));

      const old = currentWidths[partIndex];
      const delta = clampedNew - old;
      if (delta === 0) return prev;

      currentWidths[partIndex] = clampedNew;

      // Adjust the next part (or previous if this is the last) to compensate
      const adjustIndex = partIndex < underlightParts - 1 ? partIndex + 1 : partIndex - 1;
      if (adjustIndex >= 0) {
        currentWidths[adjustIndex] = Math.max(
          minPerPart,
          currentWidths[adjustIndex] - delta
        );
      }

      // Normalize so sum exactly equals totalWidth by tweaking the last segment
      let sum = currentWidths.reduce((s, w) => s + w, 0);
      const diff = totalWidth - sum;
      currentWidths[underlightParts - 1] = Math.max(
        minPerPart,
        currentWidths[underlightParts - 1] + diff
      );

      return {
        ...prev,
        underlightPartWidths: currentWidths,
        widthManuallySet: true, // user effectively changed widths
      };
    });
  };
  
  // Ensure partWindowTypes[0] is synced with raamtype when parts === 1
  useEffect(() => {
    if (config.parts === 1 && config.partWindowTypes?.[0] !== config.raamtype) {
      setConfig(prev => ({
        ...prev,
        partWindowTypes: [prev.raamtype]
      }));
    }
  }, [config.parts, config.raamtype]);
  
  const selectedRaamtype = config.parts === 1 
    ? raamtypes.find(r => r.id === config.raamtype)
    : raamtypes.find(r => r.id === (config.partWindowTypes?.[0] || 'fixed-glass'));
  // Use getColorById to handle both regular colors and RAL colors
  const selectedColorData = getColorById(config.color);
  const selectedColor = selectedColorData || colors.find(c => c.id === config.color) || { id: 'anthracite', name: 'Anthracite Grey', hex: '#3D3D3D' };
  const selectedInteriorFrameColorData = getColorById(config.interiorFrameColor);
  const selectedInteriorFrameColor = selectedInteriorFrameColorData || colors.find(c => c.id === config.interiorFrameColor) || { id: 'white', name: 'Pure White', hex: '#FFFFFF' };
  const selectedGlassTexture = glassTextures.find(g => g.id === config.glassTexture);
  const selectedGlazingType = glazingTypes.find(g => g.id === config.glazingType);
  
  return (
    <div className="h-screen w-screen overflow-hidden bg-gray-50">
      {/* Header Bar */}
      <div className="bg-slate-800 text-white px-6 py-4 shadow-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                navigate('/configurator-select', { replace: false });
              }}
              className="inline-flex items-center px-3 py-1.5 rounded-md border border-slate-600 text-sm text-white hover:bg-slate-700 transition-colors"
            >
              <span className="mr-1">←</span>
              {t('configurator.back')}
            </button>
            <div>
              <h1 className="text-2xl font-bold">{t('configurator.windowConfigurator')}</h1>
              <p className="text-sm text-slate-300 mt-0.5">{t('configurator.professionalSalesTool')}</p>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <button 
              onClick={() => setShowSavedQuotes(true)}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
            >
              {t('configurator.savedQuotes')}
            </button>
            <button 
              onClick={() => setShowQuote(true)}
              className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg font-medium transition-colors"
            >
              {t('configurator.exportQuote')}
            </button>
          </div>
        </div>
      </div>
      
      {/* Main Content */}
      <div className="flex h-[calc(100vh-80px)]">
        {/* Left Panel - Options */}
        <div className="w-full lg:w-1/3 xl:w-1/4 overflow-hidden">
          <OptionsPanel
            config={config}
            onConfigChange={handleConfigChange}
            windowTypes={windowTypes}
            profileSystems={profileSystems}
            glassTextures={glassTextures}
            glazingTypes={glazingTypes}
            colors={colors}
            openingTypes={openingTypes}
            hardware={hardware}
            accessories={accessories}
            dimensionConstraints={DIMENSION_CONSTRAINTS}
            partsOptions={partsOptions}
            overheadOptions={overheadOptions}
            woodJointOptions={woodJointOptions}
            divisionTypes={divisionTypes}
            raamtypes={raamtypes}
          />
        </div>
        
        {/* Right Panel - Drawing Board */}
        <div className="flex-1 p-6 overflow-hidden relative">
          <div ref={drawingBoardRef} className="w-full h-full">
          <Kozijnen
            width={config.width}
            height={config.height}
            windowType={selectedRaamtype}
            color={selectedColor}
              interiorFrameColor={selectedInteriorFrameColor}
            glassTexture={selectedGlassTexture}
            glazingType={selectedGlazingType}
            parts={config.parts}
            partWindowTypes={config.partWindowTypes}
            woodJoint={config.woodJoint}
            overhead={config.overhead}
              overheadParts={config.overheadParts}
              overheadHeight={config.overheadHeight}
              overheadPartWindowTypes={config.overheadPartWindowTypes}
              overheadPartWidths={config.overheadPartWidths}
            underlight={config.underlight}
            underlightParts={config.underlightParts}
            underlightHeight={config.underlightHeight}
            underlightPartWindowTypes={config.underlightPartWindowTypes}
            underlightPartWidths={config.underlightPartWidths}
            raamtypes={raamtypes}
            onPartWindowTypeChange={handlePartWindowTypeChange}
              onOverheadPartWindowTypeChange={handleOverheadPartWindowTypeChange}
            onUnderlightPartWindowTypeChange={handleUnderlightPartWindowTypeChange}
            partWidths={config.partWidths}
            onPartWidthChange={handlePartWidthChange}
              onOverheadPartWidthChange={handleOverheadPartWidthChange}
            onUnderlightPartWidthChange={handleUnderlightPartWidthChange}
              onOverheadHeightChange={(newOverheadHeight) => handleConfigChange('overheadHeight', newOverheadHeight)}
            onUnderlightHeightChange={(newUnderlightHeight) => handleConfigChange('underlightHeight', newUnderlightHeight)}
            onWidthChange={(newWidth) => handleConfigChange('width', newWidth)}
            onHeightChange={(newHeight) => handleConfigChange('height', newHeight)}
            panelColors={config.panelColors}
            panelOverheadColors={config.panelOverheadColors}
            panelUnderlightColors={config.panelUnderlightColors}
            panelLineTypes={config.panelLineTypes}
            panelOverheadLineTypes={config.panelOverheadLineTypes}
            panelUnderlightLineTypes={config.panelUnderlightLineTypes}
            onPanelColorChange={(index, colorId) => {
              setConfig(prev => {
                const newPanelColors = [...(prev.panelColors || [])];
                newPanelColors[index] = colorId;
                return { ...prev, panelColors: newPanelColors };
              });
            }}
            onPanelOverheadColorChange={(index, colorId) => {
              setConfig(prev => {
                const newPanelColors = [...(prev.panelOverheadColors || [])];
                newPanelColors[index] = colorId;
                return { ...prev, panelOverheadColors: newPanelColors };
              });
            }}
            onPanelUnderlightColorChange={(index, colorId) => {
              setConfig(prev => {
                const newPanelColors = [...(prev.panelUnderlightColors || [])];
                newPanelColors[index] = colorId;
                return { ...prev, panelUnderlightColors: newPanelColors };
              });
            }}
            onPanelLineTypeChange={(index, lineType) => {
              setConfig(prev => {
                const newPanelLineTypes = [...(prev.panelLineTypes || [])];
                newPanelLineTypes[index] = lineType;
                return { ...prev, panelLineTypes: newPanelLineTypes };
              });
            }}
            onPanelOverheadLineTypeChange={(index, lineType) => {
              setConfig(prev => {
                const newPanelLineTypes = [...(prev.panelOverheadLineTypes || [])];
                newPanelLineTypes[index] = lineType;
                return { ...prev, panelOverheadLineTypes: newPanelLineTypes };
              });
            }}
            onPanelUnderlightLineTypeChange={(index, lineType) => {
              setConfig(prev => {
                const newPanelLineTypes = [...(prev.panelUnderlightLineTypes || [])];
                newPanelLineTypes[index] = lineType;
                return { ...prev, panelUnderlightLineTypes: newPanelLineTypes };
              });
            }}
            rampDorpel={config.rampDorpel}
            onRampDorpelChange={(value) => handleConfigChange('rampDorpel', value)}
            screenElectricallyOperated={config.screenElectricallyOperated}
            rollerShutterElectricallyOperated={config.rollerShutterElectricallyOperated}
            screenColor={config.screenColor}
            rollerShutterColor={config.rollerShutterColor}
          />
          </div>
          
          {/* Toggle Summary Button - Floating on right edge */}
          <button
            onClick={() => setShowSummary(!showSummary)}
            className={`absolute right-4 top-1/2 -translate-y-1/2 z-50 bg-orange-500 hover:bg-orange-600 text-white rounded-l-lg px-3 py-4 shadow-lg transition-all duration-300 flex items-center justify-center ${
              showSummary ? 'opacity-100' : 'opacity-100'
            }`}
            title={showSummary ? 'Hide Summary' : 'Show Summary'}
          >
            <svg
              className={`w-5 h-5 transition-transform duration-300 ${showSummary ? 'rotate-0' : 'rotate-180'}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
        
        {/* Right Panel - Summary (Slide in/out) */}
        <div className={`relative overflow-hidden border-l bg-white transition-all duration-300 ease-in-out ${
          showSummary ? 'w-full lg:w-1/4 xl:w-1/5' : 'w-0 border-0'
        }`}>
          <div className={`h-full p-4 transition-opacity duration-300 ${
            showSummary ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`}>
            <Summary
              config={config}
              pricing={PRICING}
              profileSystems={profileSystems}
              glassTextures={glassTextures}
              glazingTypes={glazingTypes}
              colors={colors}
              raamtypes={raamtypes}
              hardware={hardware}
              accessories={accessories}
              woodJointOptions={woodJointOptions}
              partsOptions={partsOptions}
              overheadOptions={overheadOptions}
              divisionTypes={divisionTypes}
              drawingBoardRef={drawingBoardRef}
              pageType="kozijnen"
              onQuantityInputChange={setCurrentQuantityInput}
            />
          </div>
        </div>
      </div>
      
      {/* Quote Modal */}
      <Quote
        key={`quote-${currentQuantityInput}`}
        open={showQuote}
        onOpenChange={setShowQuote}
        config={config}
        currentQuantityInput={currentQuantityInput}
        pricing={PRICING}
        profileSystems={profileSystems}
        glassTextures={glassTextures}
        glazingTypes={glazingTypes}
        colors={colors}
        raamtypes={raamtypes}
        hardware={hardware}
        accessories={accessories}
        woodJointOptions={woodJointOptions}
        partsOptions={partsOptions}
        overheadOptions={overheadOptions}
        drawingBoardRef={drawingBoardRef}
      />
      
      {/* Saved Quotes Modal */}
      <SavedQuotes
        open={showSavedQuotes}
        onOpenChange={setShowSavedQuotes}
      />
    </div>
  );
};

export default Configurator;
